import React from 'react';
import '../styles/RequirementTable.css';

interface RequirementItem {
  description: string;
  priority: string; // 修改为普通字符串类型，以便接受任何优先级文本
}

interface RequirementTableProps {
  markdownText: string; // 改为直接接收Markdown文本
}

/**
 * 需求优先级表格组件
 * 
 * @param props 包含Markdown表格文本的属性
 * @returns 渲染的需求表格组件
 */
const RequirementTable: React.FC<RequirementTableProps> = ({ markdownText }) => {
  
  /**
   * 解析Markdown表格文本提取需求项
   * 
   * @param text Markdown表格文本
   * @returns 解析后的需求项数组
   */
  const parseRequirements = (text: string): RequirementItem[] => {
    if (!text) return [];
    
    const lines = text.split('\n').filter(line => line.trim());
    
    // 找到表头行
    const headerRowIndex = lines.findIndex(line => 
      (line.includes('| 需求描述') && line.includes('| 优先级')) ||
      (line.includes('| 需求编号') && line.includes('| 需求描述') && line.includes('| 优先级'))
    );
    
    if (headerRowIndex === -1) return [];
    
    // 判断表格类型
    const hasIdColumn = lines[headerRowIndex].includes('| 需求编号');
    
    // 跳过表头和分隔行，直接处理数据行
    const dataRows = lines.slice(headerRowIndex + 2).filter(line => 
      line.includes('|') && !line.includes('---')
    );
    
    // 解析每一行数据
    return dataRows.map(row => {
      const columns = row.split('|').filter(col => col.trim());
      
      if (hasIdColumn && columns.length >= 3) {
        // 处理带有需求编号的格式
        return {
          description: columns[1].trim(), // 第二列是描述
          priority: columns[2].trim()     // 第三列是优先级
        };
      } else if (columns.length >= 2) {
        // 处理没有需求编号的格式
        return {
          description: columns[0].trim(), // 第一列是描述
          priority: columns[1].trim()     // 第二列是优先级
        };
      }
      
      return { description: '', priority: '' };
    }).filter(item => item.description && item.priority);
  };
  
  /**
   * 获取优先级对应的CSS类名
   * 
   * @param priority 优先级文本
   * @returns 对应的CSS类名
   */
  const getPriorityClass = (priority: string): string => {
    if (priority.includes('🔥') || priority.includes('高优先级')) return 'priority-high';
    if (priority.includes('⚠️') || priority.includes('潜在风险')) return 'priority-medium';
    if (priority.includes('💡') || priority.includes('洞察')) return 'priority-insight';
    return '';
  };

  // 解析需求项
  const requirements = parseRequirements(markdownText);

  // 如果没有解析到需求，返回空
  if (requirements.length === 0) {
    return null;
  }

  return (
    <div className="req-table-container">
      <table className="req-table">
        <thead>
          <tr>
            <th>需求描述</th>
            <th>优先级</th>
          </tr>
        </thead>
        <tbody>
          {requirements.map((req, index) => (
            <tr key={index} className={getPriorityClass(req.priority)}>
              <td>{req.description}</td>
              <td className="priority-cell">{req.priority}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RequirementTable; 